import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { errorLog, infoLog, warnLog } from "../../utils/logger";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { ImmunizationPatientContact, ImmunizationSearch } from "../../models/Interfaces";
import {
  formatNumber,
  setPinAllowedDate,
  getPinAllowedDate,
  setPinAttempts,
  getPinAttempts,
  getDateTime,
} from "../../utils/data";
import { DateTime } from "luxon";
import { IconButton } from "../../components/IconButton/IconButton";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { izSearchAPIResultAtom, selectedSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { LegacyDeliverSearchPinRequestDto } from "@docket/consumer-app-client-typescript-axios";

export function AltContactsForm() {
  const [search, setSearch] = useAtom(selectedSearchAtom);
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const hasContacts = () => {
    return (
      search &&
      search.patients[0] &&
      search.patients[0].contacts &&
      search.patients[0].contacts.length > 0
    );
  };

  const contacts = hasContacts() ? search!.patients[0].contacts : [];
  const groupedContacts =
    contacts?.reduce((result, contact) => {
      if (!result[contact.type]) {
        result[contact.type] = [];
      }

      result[contact.type].push(contact);
      return result;
    }, {} as { [key: string]: ImmunizationPatientContact[] }) || {};

  const [isSearchRunning, setIsSearchRunning] = useState(false);
  const [selection, setSelection] = useState("");
  const [phoneSelection, setPhoneSelection] = useState<string | undefined>();
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const navigate = useNavigate();

  // TODO: i18n on these options. We're doing string-comparison
  const phoneOptions = ["Mobile Phone", "Landline"];
  const { t } = useTranslation();

  const next = async () => {
    let param: LegacyDeliverSearchPinRequestDto = {};
    if (selection != null && selection.length > 0) {
      if (selection.includes("@")) {
        param["email"] = selection;
      } else if (!selection.includes("none")) {
        const parsed = phoneSelection?.includes("Mobile") ? "sms" : "voice";
        param[parsed] = selection;
      }
    }

    infoLog(`contacts param ${selection} ${JSON.stringify(param)}`);
    if (param.sms || param.voice) {
      await checkForPinAttempt(param);
    } else {
      await continueFromAlert(param);
    }
  };

  const continueFromAlert = async (param: LegacyDeliverSearchPinRequestDto) => {
    infoLog(`continueFromAlert param ${JSON.stringify(param)}`);
    if (search === null) {
      errorLog(new Error("Search is somehow null!"));
      navigate("../results");
      return;
    }

    if (param.sms || param.voice || param.email) {
      setIsSearchRunning(true);
      try {
        await getAPIClient().sendImmunizationSearchPIN(search.uid, param, search.izProviderKey);
        /**
         * Do not await this since it could potentially take a long time to
         * complete and we want to navigate immediately.
         */
        fetchRecordsUntilUpdated();
        navigate("../enterpin");
      } catch (e: unknown) {
        if (e instanceof DocketAPIError) {
          setShowErrorModal(e.message);
        }
      }
    } else {
      /**
       * Do not await this since it could potentially take a long time to
       * complete and we want to navigate immediately.
       */
      fetchRecordsUntilUpdated();
      navigate("../results", { state: { isScenarioB: true } });
    }
  };

  const checkForPinAttempt = async (param: LegacyDeliverSearchPinRequestDto) => {
    const attempts = await getPinAttempts();
    const format = "YYYY-MM-DD HH:mm:ss";
    if (attempts >= 3) {
      const dateAllowed = await getPinAllowedDate();
      let compare = null;
      if (dateAllowed && getDateTime(dateAllowed.toString()).isValid) {
        compare = getDateTime(dateAllowed.toString()) < DateTime.now();
      }
      if (dateAllowed && compare) {
        //reset and allow pin call
        await setPinAttempts(true);
        await setPinAllowedDate("");
        await setPinAttempts(false);
        continueFromAlert(param);
      } else if (!dateAllowed) {
        await setPinAllowedDate(DateTime.now().plus({ minutes: 15 }).toFormat(format));
        pinAttemptAlert();
      } else {
        pinAttemptAlert();
      }
    } else {
      //increment pin attempts
      await setPinAttempts(false);
      continueFromAlert(param);
    }
  };

  const pinAttemptAlert = () => {
    setShowErrorModal(`${t("immunizations.alt_contacts_pin_limit")}`);
  };

  const deleteSearch = async () => {
    if (search === null) {
      return;
    }

    try {
      await getAPIClient().deleteIzSearch(search.uid, search.izProviderKey);
      const updatedSearches = await getAPIClient().getIzSearches(false);
      await setIzSearchAPIResult(updatedSearches.data);
      await setSearch(null);
      navigate(-1);
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const setContactNumberSelection = (number: string, selection: string) => {
    setSelection(number);
    setPhoneSelection(selection);
  };

  const contactCheckboxHandler = (value: any, index: number, phoneValue?: string) => {
    infoLog(`value ${value}, index ${index}`);
    if (index === 999) {
      setContactNumberSelection("-1", "-1");
      setSelection("none");
    } else {
      setContactNumberSelection(value, `${phoneValue ? phoneValue : phoneOptions[0]}${value}`);
    }
  };

  const getContactsCheckboxSection = () => {
    return (
      <div className="flex flex-col gap-1">
        {Object.keys(groupedContacts).map((type) => (
          <>
            {groupedContacts[type].map((contact, i) => (
              <div className="field" key={`${contact.maskedValue}-${i}`}>
                <div className="flex flex-col gap-0.5">
                  <label
                    className="radio-control text-dark mt-4 max-md:text-white dark:text-white"
                    data-testid={
                      contact?.maskedValue?.includes("@") ? "email-contact" : "phone-contact"
                    }
                  >
                    <input
                      type="radio"
                      checked={selection === contact.maskedValue}
                      name={"contactOption"}
                      onChange={() => contactCheckboxHandler(contact.maskedValue, i, undefined)}
                    />
                    &nbsp;&nbsp;
                    {contact?.maskedValue?.includes("@")
                      ? contact.maskedValue
                      : formatNumber(contact?.maskedValue!)}
                  </label>
                  {!contact?.maskedValue?.includes("@") && (
                    <div className="flex gap-1 ml-8">
                      <label className="radio-control text-dark mt-4 dark:text-white">
                        <input
                          type="radio"
                          checked={
                            selection === contact.maskedValue &&
                            phoneSelection === `${phoneOptions[0]}${contact?.maskedValue!}`
                          }
                          name={"contactSubOption"}
                          onChange={() =>
                            contactCheckboxHandler(contact.maskedValue, i, phoneOptions[0])
                          }
                        />
                        &nbsp;&nbsp;
                        {phoneOptions[0]}
                      </label>
                      <label className="radio-control text-dark mt-4 max-md:text-white dark:text-white">
                        <input
                          type="radio"
                          checked={
                            selection === contact.maskedValue &&
                            phoneSelection === `${phoneOptions[1]}${contact?.maskedValue!}`
                          }
                          name={"contactSubOption"}
                          onChange={() =>
                            contactCheckboxHandler(contact.maskedValue, i, phoneOptions[1])
                          }
                        />
                        &nbsp;&nbsp;
                        {phoneOptions[1]}
                      </label>
                    </div>
                  )}
                </div>
                <br />
              </div>
            ))}
          </>
        ))}
        <div className="field mt-6" key={`none`}>
          <label className="radio-control text-dark max-md:text-white dark:text-white">
            <input
              type="radio"
              checked={selection === "none"}
              name={"contactOption"}
              onChange={() =>
                contactCheckboxHandler(t("immunizations.alt_contacts_recognize")!, 999)
              }
            />
            &nbsp;&nbsp;{t("immunizations.alt_contacts_recognize")!}
          </label>
          <br />
        </div>
      </div>
    );
  };

  function getHeader() {
    return (
      <div className="flex">
        <h3 className="text-dark font-black text-3xl max-md:hidden dark:text-white">
          {t("immunizations.search_search_incomplete")}
        </h3>
        <IconButton
          dataTestId="deleteBtn-desktop"
          icon="trash"
          onClick={() => deleteSearch()}
          alt="delete"
          className="ml-auto max-md:hidden"
        />
        <div className="md:hidden top-[20px] right-[20px] fixed z-50">
          <IconButton
            dataTestId="deleteBtn-mobile"
            icon="trash"
            onClick={() => deleteSearch()}
            alt="delete"
          />
        </div>
      </div>
    );
  }

  const getMessage1Text = () => {
    return t("immunizations.alt_contacts_header2").split("|");
  };

  const getInfoText = () => {
    return t("immunizations.alt_contacts_pin_limit").split("|");
  };

  return (
    <div className="p-4 max-md:pt-6 max-md:px-6 max-md:pb-20">
      {getHeader()}
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
      )}
      <div className="separator max-md:hidden"></div>
      <div className="text-dark w-full flex flex-col gap-2 mobile-text-color dark:text-white">
        <p>{getMessage1Text().join(" ")}</p>
        {getContactsCheckboxSection()}
        <p className="text-sm">{getInfoText()[0]}</p>
        <button
          type="submit"
          className="btn docket-button-responsive w-fit max-md:w-full"
          onClick={next}
          disabled={
            !selection ||
            (!selection.includes("@") &&
              phoneSelection !== "-1" &&
              !phoneSelection?.includes("Mobile") &&
              !phoneSelection?.includes("Landline"))
          }
        >
          {t("generic.next")}
        </button>
      </div>
    </div>
  );
}

export default AltContactsForm;
